import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "../../lib/utils";

/**
 * MarkdownMessage Component
 * Renders markdown content with consistent styling for chat messages
 */
const MarkdownMessage = ({ content, className, isUserMessage = false }) => {
  return (
    <div
      className={cn("markdown-content !text-left w-full", className)}
      style={{ textAlign: "left", display: "block" }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Paragraphs
          p: ({ children }) => (
            <p
              className="mb-2 last:mb-0 leading-relaxed !text-left"
              style={{ textAlign: "left" }}
            >
              {children}
            </p>
          ),

          // Headings
          h1: ({ children }) => (
            <h1
              className="text-lg font-bold mb-2 mt-3 first:mt-0 !text-left"
              style={{ textAlign: "left" }}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              className="text-base font-bold mb-2 mt-3 first:mt-0 !text-left"
              style={{ textAlign: "left" }}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              className="text-sm font-bold mb-1 mt-2 first:mt-0 !text-left"
              style={{ textAlign: "left" }}
            >
              {children}
            </h3>
          ),

          // Lists
          ul: ({ children }) => (
            <ul
              className="list-disc list-outside mb-2 space-y-1.5 ml-4 !text-left"
              style={{ textAlign: "left" }}
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              className="list-decimal list-outside mb-2 space-y-1.5 ml-4 !text-left"
              style={{ textAlign: "left" }}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li
              className="leading-relaxed pl-2 !text-left"
              style={{ textAlign: "left" }}
            >
              {children}
            </li>
          ),

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "underline font-medium transition-colors",
                isUserMessage
                  ? "text-white hover:text-white/80"
                  : "text-primary hover:text-primary/80"
              )}
            >
              {children}
            </a>
          ),

          // Code
          code: ({ inline, children }) =>
            inline ? (
              <code
                className={cn(
                  "px-1.5 py-0.5 rounded text-xs font-mono",
                  isUserMessage
                    ? "bg-white/20 text-white"
                    : "bg-muted text-foreground"
                )}
              >
                {children}
              </code>
            ) : (
              <code
                className={cn(
                  "block p-3 rounded-md text-xs font-mono overflow-x-auto my-2",
                  isUserMessage
                    ? "bg-white/20 text-white"
                    : "bg-muted text-foreground"
                )}
              >
                {children}
              </code>
            ),

          // Code blocks
          pre: ({ children }) => (
            <pre
              className={cn(
                "rounded-md overflow-x-auto my-2",
                isUserMessage ? "bg-white/20" : "bg-muted"
              )}
            >
              {children}
            </pre>
          ),

          // Blockquote
          blockquote: ({ children }) => (
            <blockquote
              className={cn(
                "border-l-4 pl-3 my-2 italic !text-left",
                isUserMessage ? "border-white/50" : "border-primary/50"
              )}
            >
              {children}
            </blockquote>
          ),

          // Strong/Bold
          strong: ({ children }) => (
            <strong className="font-bold">{children}</strong>
          ),

          // Emphasis/Italic
          em: ({ children }) => <em className="italic">{children}</em>,

          // Horizontal Rule
          hr: () => (
            <hr
              className={cn(
                "my-3 border-t",
                isUserMessage ? "border-white/30" : "border-border"
              )}
            />
          ),

          // Tables
          table: ({ children }) => (
            <div className="overflow-x-auto my-2 !text-left">
              <table className="min-w-full border-collapse text-xs">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className={cn(isUserMessage ? "bg-white/20" : "bg-muted")}>
              {children}
            </thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr
              className={cn(
                "border-b",
                isUserMessage ? "border-white/20" : "border-border"
              )}
            >
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-2 py-1 !text-left font-semibold">{children}</th>
          ),
          td: ({ children }) => (
            <td className="px-2 py-1 !text-left">{children}</td>
          ),

          // Images
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt}
              className="max-w-full h-auto rounded-md my-2"
              loading="lazy"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
